<template>
  <section class="height-100-p" id="shuttle-file-search-summary-section">
    <el-form :model="editForm" :rules="editFormRules" ref="editForm" size="mini">
      <el-table
        id="shuttle-file-list"
        :data="shuttleFile.list"
        v-loading="shuttleFile.loadingSearch"
        size="mini"
        tooltip-effect="dark"
        sortable
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        @sort-change="handleSortChange"
        :default-sort = "{prop: 'consigneeAccountName', order: 'descending'}"
        stripe
      >
        <el-table-column
          prop="consigneeAccountName"
          :label="$t('routing.shuttleFile.consignee')"
          :min-width="10"
          align="left"
          header-align="center"
          sortable="custom">
          <template slot-scope="scope">
            <el-tooltip class="item" effect="dark" :enterable="false" :content="scope.row.consigneeAccountName" placement="top-start">
              <span>{{ scope.row.consigneeAccountName }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          prop="city"
          :label="$t('routing.shuttleFile.city')"
          :min-width="4"
          align="center"
          sortable="custom">
          <template slot-scope="scope">
            <el-tooltip class="item" effect="dark" :enterable="false" :content="scope.row.city" placement="top-start">
              <span>{{ scope.row.city }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          prop="stateCode"
          :label="$t('routing.shuttleFile.state')"
          :min-width="3"
          align="center"
          header-align="center"
          sortable="custom">
          <template slot-scope="scope">
            <el-tooltip class="item" effect="dark" :enterable="false" :content="scope.row.stateName" placement="top-start">
              <span>{{ scope.row.stateCode }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          prop="receivingDate"
          :label="$t('routing.shuttleFile.receivingDate')"
          :min-width="5"
          align="center"
          header-align="center"
          sortable="custom">
          <template slot-scope="scope">
            <el-tooltip class="item" effect="dark" :enterable="false" :content="scope.row.receivingDate" placement="top-start">
              <span>{{ scope.row.receivingDate | moment("MM/DD/YYYY") }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          prop="outboundDate"
          :label="$t('routing.shuttleFile.outboundDate')"
          :min-width="5"
          :max-width="7"
          align="center"
          header-align="center"
          sortable="custom">
          <template slot-scope="scope">
            <template v-if="!scope.row.edit">
              <el-tooltip class="item" effect="dark" :enterable="false" :content="scope.row.outboundDate" placement="top-start">
                <span>{{ scope.row.outboundDate | moment("MM/DD/YYYY") }}</span>
              </el-tooltip>
            </template>
            <template v-else>
              <el-form-item class="inline-input width-100p" prop="outboundDate">
                <el-date-picker
                  v-model="editForm.outboundDate"
                  format="MM/dd/yyyy"
                  :picker-options="shipDatePickerOptions"
                  type="date">
                </el-date-picker>
              </el-form-item>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="outboundWarehouseCode"
          :label="$t('routing.shuttleFile.outboundWarehouse')"
          :min-width="5"
          align="center"
          header-align="center"
          sortable="custom">
          <template slot-scope="scope">
            <template v-if="!scope.row.edit">
              <el-tooltip class="item" effect="dark" :enterable="false" :content="scope.row.outboundWarehouseName" placement="top-start">
                <span>{{ scope.row.outboundWarehouseCode }}</span>
              </el-tooltip>
            </template>
            <template v-else>
              <el-form-item class="inline-input width-100p" prop="outboundWarehouseId">
                <outbound-warehouse-select :value.sync="editForm.outboundWarehouseId" :includeOptionAll="false" ref="outbound-warehouse"/>
              </el-form-item>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="shuttle"
          :label="$t('routing.shuttleFile.shuttle')"
          :min-width="3"
          align="center"
          header-align="center"
          sortable="custom">
          <template slot-scope="scope">
            <el-tooltip class="item" effect="dark" :enterable="false" :content="getIsShuttleTooltipLabel(scope.row.shuttle)" placement="top-start">
              <i v-if="scope.row.shuttle" class="el-icon-success green-success"></i>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('common.options')"
          :min-width="4"
          align="center">
          <template slot-scope="scope">
            <el-tooltip
              v-if="showEditButton()"
              class="item el-icon-edit-tooltip"
              effect="dark"
              :enterable="false"
              :content="$t('warehouse.orderEntry.edit')"
              placement="top-start"
              >
              <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)"></i>
            </el-tooltip>
            <template v-else-if="scope.row.edit">
              <el-button icon="el-icon-success" circle size="mini" @click="handleUpdate()"></el-button>
              <el-button icon="el-icon-circle-close" circle size="mini" @click="cancelEdit(scope.row)"></el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
    <div class="pagination-footer" v-show="shuttleFile.total > 0">
      <span class="el-pagination__total" slot>
        <span class="el-pagination__title" slot>{{ $t('routing.shuttleFile.total') }}</span>
           {{ shuttleFile.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="shuttleFile.actualFilters.rows"
        :current-page.sync="shuttleFile.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="shuttleFile.total"
        layout="slot, sizes, prev, pager, next, jumper"
      >
      </el-pagination>
    </div>
  </section>
</template>

<script>
import { Message } from 'element-ui';
import { mapGetters } from 'vuex';
import moment from 'moment';
import constants from '@/utils/constants';
import { SHUTTLE_FILE_SEARCH } from '@/store/actions';
import OutboundWarehouseSelect from '../components/OutboundWarehouseSelect';

export default {
  name: 'ShuttleFileSummarySection',
  components: {
    OutboundWarehouseSelect
  },
  computed: {
    ...mapGetters(['shuttleFile']),
    sizes() {
      return constants.TABLES.DEFAULT_LIMIT_SIZES;
    }
  },
  data() {
    return {
      shipDatePickerOptions: null,
      editForm: {
        id: null,
        receivingWarehouseId: null,
        consigneeAccountId: null,
        receivingDate: null,
        outboundDate: null,
        outboundWarehouseId: null
      },
      editFormRules: {
        outboundDate: [
          {
            required: true,
            message: this.$t('routing.shuttleFile.errors.outboundDateRequired'),
            trigger: 'blur'
          }
        ],
        outboundWarehouseId: [
          {
            required: true,
            message: this.$t('routing.shuttleFile.errors.outboundWarehouseRequired'),
            trigger: 'blur'
          }
        ]
      }
    };
  },
  methods: {
    getIsShuttleTooltipLabel(shuttle) {
      return shuttle ? constants.STATUS_BOOLEAN.yes.label : constants.STATUS_BOOLEAN.no.label;
    },
    handleSearchChangeLimit(data) {
      this.$store.dispatch(SHUTTLE_FILE_SEARCH, { rows: data, page: constants.TABLES.DEFAULT_PAGE });
    },
    handleSearchChangePage(data) {
      this.$store.dispatch(SHUTTLE_FILE_SEARCH, { page: data });
    },
    handleSortChange({ prop, order }) {
      this.$store.dispatch(SHUTTLE_FILE_SEARCH, { orderField: prop, orderDirection: constants.TABLES.ORDER_DIRECTION[order] });
    },
    showEditButton() {
      return (
        this.$can(this.permissions.ROUTING.EDIT_SHUTTLE_FILE)
        && !this.shuttleFile.isEditingShuttleFile
      );
    },
    handleEdit(row) {
      this.$store.dispatch('shuttleFile/setEditingShuttleFile', true);
      row.edit = true;
      this.shipDatePickerOptions = {
        disabledDate(time) {
          return moment(time.getTime()).isBefore(row.receivingDate, 'day');
        }
      };
      this.editForm.id = row.id;
      this.editForm.receivingWarehouseId = row.receivingWarehouseId;
      this.editForm.consigneeAccountId = row.consigneeAccountId;
      this.editForm.receivingDate = row.receivingDate;
      this.editForm.outboundDate = moment(row.outboundDate, constants.DATES.DEFAULT_BACKEND_FORMAT).toDate();
      this.editForm.outboundWarehouseId = row.outboundWarehouseId;
      this.$nextTick(() => {
        this.$refs['outbound-warehouse'].setSelectedWarehouse(this.editForm.outboundWarehouseId);
      });
    },
    async confirmation() {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      let continueUpdate = true;
      try {
        const response = await this.$store
          .dispatch('shuttleFile/getUnitsToUpdateCountSummary', this.editForm);
        if (response && response.data) {
          if (response.data.pending > 0) {
            let message = this.$t('routing.shuttleFile.pendingUnitsConfirmation').replace('[unitsPending]', response.data.pending);
            if (response.data.loaded > 0) {
              message = message.concat(this.$t('routing.shuttleFile.loadedUnitsConfirmation').replace('[unitsLoaded]', response.data.loaded));
            }
            message = message.concat(this.$t('routing.shuttleFile.continueConfirmation'));
            continueUpdate = await this.$confirm(message, { dangerouslyUseHTMLString: true, confirmButtonText: this.$t('common.save') }).catch(() => false);
          } else if (response.data.shuttled !== 0 || response.data.loaded !== 0) {
            const message = this.$t('routing.shuttleFile.noUpdatedUnitsConfirmation');
            continueUpdate = await this.$confirm(message, { dangerouslyUseHTMLString: true, confirmButtonText: this.$t('common.save') }).catch(() => false);
          }
        }
      } finally {
        loading.close();
      }

      return continueUpdate;
    },
    handleUpdate() {
      this.$refs.editForm.validate(async (valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          if (await this.confirmation()) {
            const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
            this.$store
              .dispatch('shuttleFile/update', this.editForm)
              .then((response) => {
                loading.close();
                this.$message.success(response.message);
                this.clearEdit();
                this.$store.dispatch(SHUTTLE_FILE_SEARCH, {});
              })
              .catch(() => {
                loading.close();
              });
          }
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    clearEdit() {
      this.$refs.editForm.resetFields();
      this.$store.dispatch('shuttleFile/setEditingShuttleFile', false);
    },
    cancelEdit(row) {
      this.clearEdit();
      row.edit = false;
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss">
#shuttle-file-list .el-table__body {
  .el-table__row {
    .el-form-item__content {
      .el-date-editor {
        width: 100%;
      }

      input {
        padding-left: 30px;
      }
    }
  }
}
</style>
